/**
 * useCoverageAudit.ts
 *
 * Fetches the data-integrity audit matrix for /coverage/audit.
 * Endpoint requires SYSTEM_ADMIN.
 */
import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';

export interface CoverageAuditRow {
  jurisdiction_id: string;
  jurisdiction_name: string;
  jurisdiction_path: string | null;
  jurisdiction_level: string;
  total_artifacts: number;
  extracted_done: number;
  classified: number;
  chunked: number;
  embedded_searchable: number;
  governing_body_populated: number;
  attendee_rows: number;
  vote_breakdown_rows: number;
  scoped_searchable_gap: number;
  chunk_jurisdiction_mismatch: number;
}

export interface CoverageAuditCrossCutting {
  orphan_artifacts: number;
  orphan_chunks: number;
  orphan_attendees: number;
  phantom_workspaces: number;
}

export interface CoverageAuditTableStatus {
  attendees_table_exists: boolean;
  vote_breakdown_table_exists: boolean;
  governing_body_log_table_exists: boolean;
}

export interface CoverageAuditData {
  jurisdictions: CoverageAuditRow[];
  crossCutting: CoverageAuditCrossCutting;
  tableStatus: CoverageAuditTableStatus;
  refreshedAt: string;
}

export function useCoverageAudit() {
  const [data, setData] = useState<CoverageAuditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/coverage-audit');
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Forbidden — SYSTEM_ADMIN access required');
        }
        throw new Error(`coverage-audit ${res.status}`);
      }
      const json = (await res.json()) as { data: CoverageAuditData };
      setData(json.data ?? null);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to load coverage audit'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, lastRefreshed, refresh };
}
